/**
 * Ticket Form Component
 * Create/edit form with validation using react-hook-form and zod
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { TicketType, TicketPriority, TicketCreate, TicketUpdate } from '@/types';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { formatLabel } from '@/utils/helpers';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';

interface TicketFormProps {
  defaultValues?: Partial<TicketCreate | TicketUpdate>;
  onSubmit: (data: TicketCreate | TicketUpdate) => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  onCancel?: () => void;
  projectId?: string;
}

export function TicketForm({
  defaultValues,
  onSubmit,
  isSubmitting,
  submitLabel = 'Create Ticket',
  onCancel,
  projectId,
}: TicketFormProps) {
  const { data: members = [] } = useQuery({
    queryKey: ['project-members', projectId],
    queryFn: async () => {
      if (!projectId) return [];
      const res = await apiClient.get("/projects/" + projectId + "/members");
      return res.data;
    },
    enabled: !!projectId,
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const res = await apiClient.get('/groups');
      return res.data;
    },
  });
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: '',
      description: '',
      type: TicketType.TASK,
      priority: TicketPriority.MEDIUM,
      story_points: undefined,
      category: '',
      subcategory: '',
      assignee_id: '',
      assigned_group_id: '',
      ...defaultValues,
    },
  });

  const watchType = watch('type');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Title */}
      <div>
        <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="title"
          {...register('title', { required: 'Title is required' })}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          placeholder="Enter ticket title"
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          id="description"
          {...register('description')}
          rows={6}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          placeholder="Provide detailed information about the ticket"
        />
      </div>

      {/* Type and Priority */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
            Type <span className="text-red-500">*</span>
          </label>
          <select
            id="type"
            {...register('type', { required: 'Type is required' })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          >
            {Object.values(TicketType).map((type) => (
              <option key={type} value={type}>
                {formatLabel(type)}
              </option>
            ))}
          </select>
          {errors.type && (
            <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
            Priority <span className="text-red-500">*</span>
          </label>
          <select
            id="priority"
            {...register('priority', { required: 'Priority is required' })}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          >
            {Object.values(TicketPriority).map((priority) => (
              <option key={priority} value={priority}>
                {formatLabel(priority)}
              </option>
            ))}
          </select>
          {errors.priority && (
            <p className="mt-1 text-sm text-red-600">{errors.priority.message}</p>
          )}
        </div>
      </div>

      {/* Category and Subcategory */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <input
            type="text"
            id="category"
            {...register('category')}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            placeholder="e.g., Authentication"
          />
        </div>

        <div>
          <label htmlFor="subcategory" className="block text-sm font-medium text-gray-700 mb-1">
            Subcategory
          </label>
          <input
            type="text"
            id="subcategory"
            {...register('subcategory')}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            placeholder="e.g., Login"
          />
        </div>
      </div>

      {/* Story Points (for user stories and tasks) */}
      {(watchType === TicketType.USER_STORY || watchType === TicketType.TASK) && (
        <div>
          <label htmlFor="story_points" className="block text-sm font-medium text-gray-700 mb-1">
            Story Points
          </label>
          <input
            type="number"
            id="story_points"
            {...register('story_points', { valueAsNumber: true })}
            min="0"
            max="100"
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            placeholder="1, 2, 3, 5, 8, 13..."
          />
        </div>
      )}

      {/* Assign to Group */}
      <div>
        <label htmlFor="assigned_group_id" className="block text-sm font-medium text-gray-700 mb-1">
          Assign to Group
        </label>
        <select
          id="assigned_group_id"
          {...register('assigned_group_id')}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
        >
          <option value="">No group</option>
          {groups.map((g: any) => (
            <option key={g.id} value={g.id}>
              {g.name} ({g.group_type})
            </option>
          ))}
        </select>
      </div>

      {/* Assignee */}
      <div>
        <label htmlFor="assignee_id" className="block text-sm font-medium text-gray-700 mb-1">
          Assign To
        </label>
        <select
          id="assignee_id"
          {...register('assignee_id')}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
        >
          <option value="">Unassigned (no auto-assignment)</option>
          {members.map((m: any) => (
            <option key={m.user_id} value={m.user_id}>
              {m.user?.first_name ? m.user.first_name + ' ' + (m.user.last_name || '') : m.user_id} ({m.role})
            </option>
          ))}
        </select>
      </div>

      {/* Form actions */}
      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isSubmitting}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Saving...
            </>
          ) : (
            submitLabel
          )}
        </button>
      </div>
    </form>
  );
}
